import { useState, useEffect } from "react";
import { getDoc, doc } from "firebase/firestore";
import { useParams, useLocation, useSearchParams } from "react-router-dom";
import { 
  AiFillCheckCircle, 
  AiOutlineArrowLeft, 
  AiOutlineArrowRight,
  AiOutlineDollarCircle,
  AiOutlineSwap,
  AiOutlineClockCircle,
  AiOutlineUser,
  AiOutlineTeam
} from "react-icons/ai";
import { 
  FaWhatsapp, 
  FaPhone, 
  FaCar, 
  FaGasPump, 
  FaCogs, 
  FaCalendarAlt,
  FaUsers,
  FaRoute,
  FaChair,
  FaLuggageCart,
  FaClock,
  FaMapMarkerAlt,
  FaSmokingBan,
  FaPaw,
  FaFemale,
  FaTachometerAlt
} from "react-icons/fa";
import { GiMoneyStack, GiTakeMyMoney } from "react-icons/gi";
import { MdPayment, MdVerified, MdLocationOn } from "react-icons/md";
import { ImFire } from "react-icons/im";
import { db } from "../firebase.config";
import ScaleSpinner from "../components/feedback/ScaleSpinner";
import { Helmet } from "react-helmet";
import { useColorModeValue } from "@chakra-ui/react";
import moment from "moment";

const ListingDetails = () => {
  const [carListing, setCarListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [showFullScreen, setShowFullScreen] = useState(false);
  const [error, setError] = useState(null);

  const params = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const param = location.state;

  const cardBg = useColorModeValue("white", "gray.800");
  const textColor = useColorModeValue("gray.800", "white");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  const formatPrice = (price) => {
    if (!price) return "0";
    return new Intl.NumberFormat('en-PK').format(price);
  };

  const calculateMonthlyInstallment = () => {
    if (!carListing?.installmentsAvailable || !carListing?.price) return 0;
    
    const principal = parseFloat(carListing.price) - parseFloat(carListing.downPayment || 0);
    if (principal <= 0) return 0;
    
    const monthlyRate = (parseFloat(carListing.interestRate) || 5) / 100 / 12;
    const months = parseInt(carListing.installmentPeriod) || 12;
    
    const monthlyPayment = (principal * monthlyRate * Math.pow(1 + monthlyRate, months)) / 
                          (Math.pow(1 + monthlyRate, months) - 1);
    
    return Math.round(monthlyPayment);
  };

  const discountPercentage = carListing?.offer && carListing?.price && carListing?.discountedPrice
    ? Math.round(((carListing.price - carListing.discountedPrice) / carListing.price) * 100)
    : 0;

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setShowFullScreen(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  useEffect(() => {
    const fetchCarListing = async () => {
      try {
        const sellerId = searchParams.get("sellerId")?.trim();
        const type = searchParams.get("type")?.trim();
        let docRef;

        console.log("Fetching listing for:", params.listingId);

        if (type) {
          if (type === "featured-bike") {
            docRef = doc(db, "featured-bikes", params.listingId);
          } else if (type === "featured-seller" || type === "featured-seller-listing") {
            docRef = doc(db, "Feature-Seller", sellerId, "listings", params.listingId);
          }
        } else {
          docRef = doc(db, "cars", params.listingId);
        }

        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setCarListing(docSnap.data());
        } else {
          setError("Listing not found");
        }
      } catch (error) {
        console.error("Error fetching listing:", error);
        setError("Failed to load listing details");
      } finally {
        setLoading(false);
      }
    };

    fetchCarListing();
  }, [params.listingId, param, searchParams]);

  const handleWhatsAppContact = () => {
    let phone = "+923487172565";

    if (!phone.startsWith("+")) {
      phone = "+92" + phone.replace(/^0+/, "");
    }

    let message = `Hello, I am interested in your ${
      carListing?.type === "ride" ? "ride" : "vehicle"
    }: ${carListing?.manufacturer || ""} ${carListing?.modelName || ""}`;
    
    if (carListing?.type === "ride") {
      message += `\nRoute: ${carListing?.fromCity || ""} → ${carListing?.toCity || ""}`;
      message += `\nDeparture: ${carListing?.departureDate || ""} at ${carListing?.departureTime || ""}`;
      message += `\nSeats: ${carListing?.availableSeats || 0}`;
      message += `\nPrice: PKR ${formatPrice(carListing?.price)} per seat`;
    } else {
      message += `\nModel: ${carListing?.modelYear || ""}`;
      message += `\nPrice: PKR ${formatPrice(carListing?.price)}`;
    }
    
    if (carListing?.installmentsAvailable) {
      message += `\n\nI see you offer installments. Could you provide more details?`;
    }
    if (carListing?.exchangeAvailable) {
      message += `\n\nI'm also interested in the exchange option. Could you tell me more about what you're looking for?`;
    }
    
    message += `\n\nIs this still available? I found it on NCPWheels.`;

    const url = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
    window.open(url, "_blank");
  };

  const handleCallContact = () => {
    window.location.href = `tel:+923487172565`;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <ScaleSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4">
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 max-w-md">
          <p className="font-bold">Error</p>
          <p>{error}</p>
        </div>
        <a href="/" className="mt-4 text-blue-600 hover:underline">
          Return to homepage
        </a>
      </div>
    );
  }

  if (!carListing) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4">
        <div className="bg-blue-100 border-l-4 border-blue-500 text-blue-700 p-4 max-w-md">
          <p className="font-bold">No Listing Found</p>
          <p>The requested listing could not be found.</p>
        </div>
        <a href="/" className="mt-4 text-blue-600 hover:underline">
          Browse other listings
        </a>
      </div>
    );
  }

  const isRideCategory = carListing.type === "ride";
  const isRentCategory = carListing.type === "rent";
  const isSaleCategory = carListing.type === "sale";

  const getPriceLabel = () => {
    if (isRideCategory) return "Per Seat";
    if (isRentCategory) return "Per Day";
    return "Price";
  };

  const getMainTitle = () => {
    if (isRideCategory) {
      return `Ride: ${carListing.fromCity || ""} → ${carListing.toCity || ""}`;
    }
    return `${carListing.manufacturer || ""} ${carListing.modelName || ""}`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>
          {isRideCategory 
            ? `Ride from ${carListing.fromCity} to ${carListing.toCity} | NCPWheels`
            : `${carListing.manufacturer} ${carListing.modelName} (${carListing.modelYear}) | NCPWheels`}
        </title>
        <meta
          name="description"
          content={isRideCategory
            ? `Find a ride from ${carListing.fromCity} to ${carListing.toCity} on ${carListing.departureDate}. ${carListing.availableSeats} seats available at PKR ${formatPrice(carListing.price)} per seat.`
            : `View details for ${carListing.manufacturer} ${carListing.modelName} - ${carListing.modelYear} model available at PKR ${formatPrice(carListing.price)}`}
        />
      </Helmet>

      {/* Fullscreen Image Viewer */}
      {showFullScreen && carListing.carImages && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center">
          <div className="relative w-full h-full flex items-center justify-center">
            <button
              className="absolute top-5 right-5 text-white text-4xl p-2 hover:bg-white hover:bg-opacity-20 rounded-full transition-all"
              onClick={() => setShowFullScreen(false)}
            >
              &times;
            </button>

            <button
              className="absolute left-5 text-white text-4xl p-3 hover:bg-white hover:bg-opacity-20 rounded-full transition-all"
              onClick={(e) => {
                e.stopPropagation();
                setActiveImageIndex((prev) =>
                  prev === 0 ? carListing.carImages.length - 1 : prev - 1
                );
              }}
            >
              <AiOutlineArrowLeft />
            </button>

            <img
              src={carListing.carImages[activeImageIndex]}
              alt="Fullscreen view"
              className="max-w-full max-h-full object-contain p-4"
              onClick={(e) => e.stopPropagation()}
            />

            <button
              className="absolute right-5 text-white text-4xl p-3 hover:bg-white hover:bg-opacity-20 rounded-full transition-all"
              onClick={(e) => {
                e.stopPropagation();
                setActiveImageIndex((prev) =>
                  prev === carListing.carImages.length - 1 ? 0 : prev + 1
                );
              }}
            >
              <AiOutlineArrowRight />
            </button>

            <div className="absolute bottom-5 text-white">
              {activeImageIndex + 1} / {carListing.carImages.length}
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Image Gallery - Original styling restored */}
        {carListing.carImages && carListing.carImages.length > 0 && (
          <div className="mb-8 rounded-xl overflow-hidden shadow-xl">
            <div className="relative h-74 sm:h-96 w-full bg-gray-100">
              <img
                src={carListing.carImages[activeImageIndex]}
                alt={`${carListing.manufacturer} ${carListing.modelName}`}
                className="w-full h-full object-cover cursor-pointer"
                onClick={() => setShowFullScreen(true)}
              />
              
              {/* Payment Option Badges Overlay */}
              <div className="absolute top-4 left-4 flex flex-col space-y-2">
                {!isRideCategory && carListing.installmentsAvailable && (
                  <div className="flex items-center bg-green-500 text-white px-3 py-1.5 rounded-lg shadow-lg">
                    <GiMoneyStack className="mr-2 text-lg" />
                    <span className="font-semibold text-sm">Installments Available</span>
                  </div>
                )}
                
                {!isRideCategory && carListing.exchangeAvailable && (
                  <div className="flex items-center bg-purple-500 text-white px-3 py-1.5 rounded-lg shadow-lg">
                    <AiOutlineSwap className="mr-2 text-lg" />
                    <span className="font-semibold text-sm">Exchange Available</span>
                  </div>
                )}
              </div>

              {/* Discount Badge */}
              {isSaleCategory && carListing.offer && discountPercentage > 0 && (
                <div className="absolute top-4 right-4 flex items-center bg-red-500 text-white px-4 py-2 rounded-lg shadow-lg">
                  <ImFire className="mr-2 text-xl" />
                  <span className="font-bold">{discountPercentage}% OFF</span>
                </div>
              )}

              <div className="absolute bottom-4 left-0 right-0 flex justify-center space-x-2">
                {carListing.carImages.map((_, index) => (
                  <button
                    key={index}
                    className={`w-3 h-3 rounded-full ${
                      index === activeImageIndex ? "bg-blue-600" : "bg-white bg-opacity-50"
                    }`}
                    onClick={() => setActiveImageIndex(index)}
                  />
                ))}
              </div>
            </div>
            <div className="grid grid-cols-4 sm:grid-cols-6 gap-1 p-1 bg-gray-50">
              {carListing.carImages.slice(0, 6).map((img, index) => (
                <div
                  key={index}
                  className="aspect-square cursor-pointer"
                  onClick={() => setActiveImageIndex(index)}
                >
                  <img
                    src={img}
                    alt={`Thumbnail ${index + 1}`}
                    className={`w-full h-full object-cover ${
                      index === activeImageIndex
                        ? "ring-2 ring-blue-500"
                        : "opacity-80 hover:opacity-100"
                    }`}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Vehicle Info */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Details */}
          <div className="lg:col-span-2">
            <div className="mb-6">
              <h1 className="text-3xl sm:text-4xl font-bold text-blue-800">
                {getMainTitle()}
              </h1>
              <div className="flex flex-wrap items-center gap-3 mt-2">
                {!isRideCategory ? (
                  <>
                    <span className="inline-flex items-center px-3 py-1 rounded-full bg-blue-100 text-blue-800">
                      <FaCalendarAlt className="mr-1" /> {carListing.modelYear}
                    </span>
                    <span className="inline-flex items-center px-3 py-1 rounded-full bg-blue-100 text-blue-800">
                      <FaCogs className="mr-1" /> {carListing.transmission}
                    </span>
                    <span className="inline-flex items-center px-3 py-1 rounded-full bg-blue-100 text-blue-800">
                      <FaCar className="mr-1" /> {carListing.color}
                    </span>
                  </>
                ) : (
                  <>
                    <span className="inline-flex items-center px-3 py-1 rounded-full bg-purple-100 text-purple-800">
                      <FaUsers className="mr-1" /> {carListing.availableSeats} seats
                    </span>
                    <span className="inline-flex items-center px-3 py-1 rounded-full bg-purple-100 text-purple-800">
                      <FaClock className="mr-1" /> {carListing.departureTime || "TBD"}
                    </span>
                    <span className="inline-flex items-center px-3 py-1 rounded-full bg-purple-100 text-purple-800">
                      {carListing.rideType === "round-trip" ? "Round Trip" : "One Way"}
                    </span>
                  </>
                )}
                {isSaleCategory && carListing.offer && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full bg-red-100 text-red-800">
                    Special Offer
                  </span>
                )}
              </div>
            </div>

            {/* Price Section */}
            <div className={`mb-8 p-6 rounded-xl ${
              isRideCategory 
                ? "bg-gradient-to-r from-purple-50 to-pink-50" 
                : "bg-gradient-to-r from-blue-50 to-blue-100"
            }`}>
              <div className="flex flex-col">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-600">{getPriceLabel()}</h3>
                    <div className="flex items-baseline">
                      {isSaleCategory && carListing.offer && (
                        <span className="text-xl line-through text-gray-500 mr-3">
                          PKR {formatPrice(carListing.price)}
                        </span>
                      )}
                      <span className={`text-3xl font-bold ${
                        isRideCategory ? "text-purple-800" : "text-blue-800"
                      }`}>
                        PKR {formatPrice(carListing.offer ? carListing.discountedPrice : carListing.price)}
                      </span>
                    </div>
                    {isSaleCategory && carListing.offer && (
                      <div className="mt-2 text-sm text-red-600">
                        Save PKR {formatPrice(carListing.price - carListing.discountedPrice)}!
                      </div>
                    )}
                    {isRideCategory && (
                      <div className="mt-2 text-sm text-purple-600">
                        Total trip cost: PKR {formatPrice(carListing.price * carListing.availableSeats)}
                      </div>
                    )}
                  </div>
                  <div className="mt-4 sm:mt-0 flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={handleWhatsAppContact}
                      className="flex items-center justify-center px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors w-full sm:w-auto"
                    >
                      <FaWhatsapp className="mr-2 text-xl" />
                      WhatsApp
                    </button>
                    <button
                      onClick={handleCallContact}
                      className="flex items-center justify-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors w-full sm:w-auto"
                    >
                      <FaPhone className="mr-2" />
                      Call
                    </button>
                  </div>
                </div>

                {/* Installment Details - Sale only */}
                {isSaleCategory && carListing.installmentsAvailable && (
                  <div className="mt-6 p-4 bg-white rounded-lg border border-green-200">
                    <h4 className="font-semibold text-green-800 flex items-center mb-3">
                      <GiMoneyStack className="mr-2 text-xl" />
                      Installment Plan Details
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      <div>
                        <p className="text-sm text-gray-600">Down Payment</p>
                        <p className="font-bold text-gray-900">PKR {formatPrice(carListing.downPayment)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Monthly Payment</p>
                        <p className="font-bold text-green-600">PKR {formatPrice(calculateMonthlyInstallment())}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Duration</p>
                        <p className="font-bold text-gray-900">{carListing.installmentPeriod} months</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Exchange Details - Enhanced for Sale */}
                {isSaleCategory && carListing.exchangeAvailable && (
                  <div className="mt-4 p-4 bg-white rounded-lg border border-purple-200">
                    <h4 className="font-semibold text-purple-800 flex items-center mb-3">
                      <AiOutlineSwap className="mr-2 text-xl" />
                      Looking to Exchange With
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {carListing.exchangeManufacturer && (
                        <div>
                          <p className="text-sm text-gray-600">Manufacturer</p>
                          <p className="font-semibold text-gray-900">{carListing.exchangeManufacturer}</p>
                        </div>
                      )}
                      {carListing.exchangeModel && (
                        <div>
                          <p className="text-sm text-gray-600">Model</p>
                          <p className="font-semibold text-gray-900">{carListing.exchangeModel}</p>
                        </div>
                      )}
                      {carListing.exchangeYear && (
                        <div>
                          <p className="text-sm text-gray-600">Year</p>
                          <p className="font-semibold text-gray-900">{carListing.exchangeYear}</p>
                        </div>
                      )}
                      {carListing.exchangeMileage > 0 && (
                        <div>
                          <p className="text-sm text-gray-600">Max Mileage</p>
                          <p className="font-semibold text-gray-900">{formatPrice(carListing.exchangeMileage)} km</p>
                        </div>
                      )}
                      {carListing.exchangeCondition && (
                        <div>
                          <p className="text-sm text-gray-600">Condition</p>
                          <p className="font-semibold text-gray-900 capitalize">{carListing.exchangeCondition}</p>
                        </div>
                      )}
                    </div>
                    <div className="mt-3 pt-2 border-t border-purple-100">
                      <p className="text-xs text-gray-500 flex items-center gap-1">
                        <AiOutlineSwap className="text-purple-500" />
                        Seller is open to exchanging their car for the right vehicle
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Ride Sharing Specific Details */}
            {isRideCategory && (
              <div className="mb-8">
                <h2 className="text-2xl font-bold mb-4 text-purple-800 border-b pb-2">
                  Ride Details
                </h2>
                <div className="grid grid-cols-1 gap-4">
                  <div className="flex items-center p-4 bg-white rounded-lg shadow-sm border border-gray-100">
                    <FaRoute className="text-purple-600 text-2xl mr-3" />
                    <div>
                      <div className="font-semibold text-gray-600">Route</div>
                      <div className="text-lg font-bold text-gray-800">
                        {carListing.fromCity} → {carListing.toCity}
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="flex items-center p-4 bg-white rounded-lg shadow-sm border border-gray-100">
                      <FaCalendarAlt className="text-purple-600 text-xl mr-3" />
                      <div>
                        <div className="font-semibold text-gray-600">Departure Date</div>
                        <div className="text-gray-800">{moment(carListing.departureDate).format("MMMM D, YYYY")}</div>
                      </div>
                    </div>
                    
                    <div className="flex items-center p-4 bg-white rounded-lg shadow-sm border border-gray-100">
                      <FaClock className="text-purple-600 text-xl mr-3" />
                      <div>
                        <div className="font-semibold text-gray-600">Departure Time</div>
                        <div className="text-gray-800">{carListing.departureTime || "To be confirmed"}</div>
                      </div>
                    </div>
                    
                    <div className="flex items-center p-4 bg-white rounded-lg shadow-sm border border-gray-100">
                      <FaChair className="text-purple-600 text-xl mr-3" />
                      <div>
                        <div className="font-semibold text-gray-600">Available Seats</div>
                        <div className="text-gray-800">{carListing.availableSeats} seats</div>
                      </div>
                    </div>
                    
                    <div className="flex items-center p-4 bg-white rounded-lg shadow-sm border border-gray-100">
                      <FaLuggageCart className="text-purple-600 text-xl mr-3" />
                      <div>
                        <div className="font-semibold text-gray-600">Luggage Space</div>
                        <div className="text-gray-800">{carListing.luggageSpace || "Standard"}</div>
                      </div>
                    </div>
                  </div>

                  {/* Round Trip Details */}
                  {carListing.rideType === "round-trip" && (
                    <div className="mt-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
                      <h4 className="font-semibold text-purple-800 flex items-center mb-3">
                        <AiOutlineClockCircle className="mr-2" />
                        Return Trip Details
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-gray-600">Return Date</p>
                          <p className="font-bold text-gray-900">
                            {carListing.returnDate ? moment(carListing.returnDate).format("MMMM D, YYYY") : "TBD"}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Return Time</p>
                          <p className="font-bold text-gray-900">{carListing.returnTime || "TBD"}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Rider Preferences */}
                  {(carListing.smoking || carListing.pets || carListing.femaleOnly) && (
                    <div className="mt-4 p-4 bg-white rounded-lg shadow-sm border border-gray-100">
                      <h4 className="font-semibold text-purple-800 flex items-center mb-3">
                        <AiOutlineUser className="mr-2" />
                        Rider Preferences
                      </h4>
                      <div className="flex flex-wrap gap-3">
                        {carListing.smoking && (
                          <span className="inline-flex items-center px-3 py-1 rounded-full bg-gray-100 text-gray-700">
                            <FaSmokingBan className="mr-1" /> No Smoking
                          </span>
                        )}
                        {carListing.pets && (
                          <span className="inline-flex items-center px-3 py-1 rounded-full bg-gray-100 text-gray-700">
                            <FaPaw className="mr-1" /> Pets Allowed
                          </span>
                        )}
                        {carListing.femaleOnly && (
                          <span className="inline-flex items-center px-3 py-1 rounded-full bg-pink-100 text-pink-700">
                            <FaFemale className="mr-1" /> Female Only
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Specifications - For Sale/Rent */}
            {!isRideCategory && (
              <div className="mb-8">
                <h2 className={`text-2xl font-bold mb-4 border-b pb-2 ${
                  isRentCategory ? "text-indigo-800" : "text-blue-800"
                }`}>
                  Specifications
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { icon: <FaCar />, label: "Manufacturer", value: carListing.manufacturer },
                    { icon: <FaCar />, label: "Model", value: carListing.modelName },
                    { icon: <FaCalendarAlt />, label: "Year", value: carListing.modelYear },
                    { icon: <FaTachometerAlt />, label: "Mileage", value: `${carListing.mileage} km` },
                    { icon: <FaCogs />, label: "Transmission", value: carListing.transmission },
                    { icon: <AiFillCheckCircle />, label: "Color", value: carListing.color },
                    { icon: <MdLocationOn />, label: "City", value: carListing.city },
                    { icon: <FaMapMarkerAlt />, label: "Location", value: carListing.location },
                  ].map((spec, index) => (
                    <div
                      key={index}
                      className="flex items-center p-3 bg-white rounded-lg shadow-sm border border-gray-100"
                    >
                      <div className={`mr-3 text-xl ${
                        isRentCategory ? "text-indigo-600" : "text-blue-600"
                      }`}>{spec.icon}</div>
                      <div>
                        <div className="font-semibold text-gray-600">{spec.label}</div>
                        <div className="text-gray-800">{spec.value}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {carListing.notes && (
              <div className="p-6 bg-white rounded-xl shadow-sm border border-gray-100">
                <h3 className="text-xl font-bold mb-3 text-blue-800 border-b pb-2">
                  {isRideCategory ? "Additional Notes" : "Seller's Notes"}
                </h3>
                <p className="text-gray-700 whitespace-pre-wrap">{carListing.notes}</p>
              </div>
            )}

            <div className="p-6 bg-white rounded-xl shadow-sm border border-gray-100">
              <h3 className="text-xl font-bold mb-3 text-blue-800 border-b pb-2">
                Contact NCPWheels Sales
              </h3>
              <div className="space-y-3">
                <div className="flex items-center p-2 bg-gray-50 rounded-lg">
                  <FaPhone className="text-blue-600 mr-2" />
                  <span className="font-medium">{"+92 348 717 2565"}</span>
                </div>
                <button
                  onClick={handleWhatsAppContact}
                  className="w-full flex items-center justify-center px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <FaWhatsapp className="mr-2 text-lg" />
                  Send WhatsApp Message
                </button>
                <button
                  onClick={handleCallContact}
                  className="w-full flex items-center justify-center px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <FaPhone className="mr-2" />
                  Call Now
                </button>
              </div>
            </div>

            {/* Features & Options - For Sale/Rent */}
            {!isRideCategory && carListing.options && Object.values(carListing.options).some(v => v === true) && (
              <div className="p-6 bg-white rounded-xl shadow-sm border border-gray-100">
                <h3 className={`text-xl font-bold mb-3 border-b pb-2 ${
                  isRentCategory ? "text-indigo-800" : "text-blue-800"
                }`}>
                  Features & Options
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(carListing.options).map(([key, value]) => {
                    if (value) {
                      const featureNames = {
                        leatherSeats: "Leather Seats",
                        airBags: "Air Bags",
                        bluetooth: "Bluetooth",
                        fogLights: "Fog Lights",
                        ac: "Air Conditioning",
                        usbPort: "USB Port"
                      };
                      return (
                        <div key={key} className="flex items-center">
                          <AiFillCheckCircle className="text-green-500 mr-2 flex-shrink-0" />
                          <span className="text-gray-700">{featureNames[key] || key}</span>
                        </div>
                      );
                    }
                    return null;
                  })}
                </div>
              </div>
            )}

            {/* Listing Type Badge */}
            <div className={`p-4 rounded-xl text-center ${
              isRideCategory 
                ? "bg-gradient-to-r from-purple-100 to-pink-100" 
                : isRentCategory
                ? "bg-gradient-to-r from-indigo-100 to-blue-100"
                : "bg-gradient-to-r from-blue-100 to-cyan-100"
            }`}>
              <p className="text-gray-600">Listing Type</p>
              <p className={`text-2xl font-bold ${
                isRideCategory ? "text-purple-700" : isRentCategory ? "text-indigo-700" : "text-blue-700"
              }`}>
                {isRideCategory ? "Ride Sharing" : isRentCategory ? "For Rent" : "For Sale"}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListingDetails;